import { Component, Inject, OnInit } from '@angular/core';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { AttributeAssignmentRow } from 'src/app/models/attribute-assignment-row';
import { AttributeAssignmentDialogData } from './models/attribute-assignment-dialog-data';
import { AttributeAssignmentDialogResult } from './models/attribute-assignment-dialog-result';

import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { Observable, switchMap, tap } from 'rxjs';
import { Attribute } from 'src/app/models/attribute';
import { PropertyType } from 'src/app/models/enums/property-type';
import { RestApiConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-configuration';
import { AutocompletionService } from 'src/app/services/autocompletion.service';
import { RestApiConfigurationsService } from 'src/app/services/restApi/rest-api-configurations.service';
import { FormFocusDirective } from '../../../../controls/directives/form-focus.directive';

export class ValueText {
    value: string;
    text: string;
}

@Component({
    selector: 'app-attribute-assignment-dialog',
    templateUrl: './attribute-assignment-dialog.component.html',
    styleUrls: ['./attribute-assignment-dialog.component.scss'],
    imports: [
        MatCardModule,
        FormsModule,
        FormFocusDirective,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        MatInputModule,
        MatButtonModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        CommonModule,
        MatDatepickerModule,
        NgxMatDatepickerInput,
        NgxMatDatetimepicker,
    ],
})
export class AttributeAssignmentDialogComponent implements OnInit {
    //dialogData: AttributeAssignmentDialogData;
    row: AttributeAssignmentRow;

    public PropertyType = PropertyType;
    public attributeValue: string;
    public attributeName: string;
    public attributeFormat: string;
    public attributeId: number;
    public selectedAttribute: Attribute = null;

    public listAttributeValueCtrl: UntypedFormControl;
    public inputCtrl: FormControl = new FormControl();
    public listValues: ValueText[];
    public formatDisabled: boolean = true;

    /**
     * Liste des valeurs proposées par l'autocomplétion.
     * (Utilisé uniquement par le type d'attribut 'Autocompletion par API')
     */
    public autocompletionValues: Observable<string[]>;

    /**
     * Instance de la configuration d'API.
     * (Utilisé uniquement par le type d'attribut 'Autocompletion par API')
     */
    public restApiConfiguration: RestApiConfiguration = null;

    /**
     * Valeur selectionnée dans le cas d'un attribut de type 'Autocompletion par API'.
     */
    private autoCompletionSelectedValue: string = '';

    /**
     * Valeur indiquant que l'attribut n'est pas renseigné alors qu'il est obligatoire.
     */
    public isRequiredError: boolean = false;

    constructor(
        public dialogRef: MatDialogRef<AttributeAssignmentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AttributeAssignmentDialogData,
        private autocompletionService: AutocompletionService,
        private restApiConfigurationsService: RestApiConfigurationsService
    ) {
        this.listAttributeValueCtrl = new UntypedFormControl();
        if (data.row != null) {
            this.row = data.row;
            this.attributeName = data.row.attributeName;
            this.attributeValue = data.row.attributeValue;
            this.attributeFormat = data.row.attributeFormat;
            this.attributeId = data.row.attributeId;
            this.checkIfFormatInputEnabled(this.attributeId);
            this.selectAttribute(this.attributeId);
        }

        this.listAttributeValueCtrl.valueChanges.subscribe((x) => {
            console.log('value change:', x);
            this.attributeValue = x;
        });

        if (this.selectedAttribute && this.selectedAttribute.type === PropertyType.AutocompletionByApi) {
            // Chargement de la configuration d'API
            this.initAutoCompletion(this.selectedAttribute);
        }
    }

    ngOnInit() {
        // Définir la valeur initiale sans ouvrir la liste déroulante
        if (this.attributeId) {
            this.listAttributeValueCtrl.setValue(this.attributeValue, { emitEvent: false });
        }
    }

    onConfirm(): void {
        if (this.row == null) {
            this.row = new AttributeAssignmentRow();
        }

        this.row.attributeId = this.attributeId;

        let attribute = this.data.attributes.find((x) => x.id == this.attributeId);
        this.row.attributeName = attribute.header;
        this.row.attributeValue = this.attributeValue;
        this.row.attributeFormat = this.attributeFormat;

        this.checkIfFormatInputEnabled(this.attributeId);

        var result = new AttributeAssignmentDialogResult(true, this.row);
        // Close the dialog, return true
        this.dialogRef.close(result);
    }

    onDismiss(): void {
        var result = new AttributeAssignmentDialogResult(false);
        // Close the dialog, return false
        this.dialogRef.close(result);
    }

    /**
     * Levé lorsque l'utilisateur change d'attribut.
     */
    public onSelectedAttributeChange(event: MatSelectChange): void {
        this.selectAttribute(event.value);
        this.checkIfFormatInputEnabled(event.value);

        if (this.selectedAttribute && this.selectedAttribute.type === PropertyType.AutocompletionByApi) {
            // Chargement de la configuration d'API
            this.initAutoCompletion(this.selectedAttribute);
        }
    }

    /**
     * Appelée lorsqu'une valeur de la liste proposée par l'autocomplétion est selectionnée.
     * @param event Événement.
     */
    public onAutocompleteItemSelected(event: MatAutocompleteSelectedEvent): void {
        let value: string = event.option.value;

        this.isRequiredError = false;

        this.autoCompletionSelectedValue = value;
        this.listAttributeValueCtrl.setValue(value);
    }

    /**
     * Appelée lorsque la liste proposée par l'autocomplétion est fermée.
     */
    public onAutocompleteClosed(): void {
        console.log('closed');
        this.isRequiredError = false;

        let value = this.listAttributeValueCtrl.value;

        if (this.autoCompletionSelectedValue !== value && value !== '') {
            this.listAttributeValueCtrl.setValue(this.autoCompletionSelectedValue);
        }

        if (!!!value || value === '') {
            this.isRequiredError = true;
        }
    }

    /**
     * Vérifie si le champ format doit être activé.
     * @param attributeId Id de l'attribut sélectionné.
     */
    private checkIfFormatInputEnabled(attributeId: number) {
        const selectedAttribute = this.data.attributes.find((x) => x.id === attributeId);

        this.inputCtrl.setValue('');
        this.listAttributeValueCtrl.setValue('');
        this.inputCtrl.clearValidators();
        this.listAttributeValueCtrl.clearValidators();

        if (selectedAttribute.mandatory === true) {
            if (selectedAttribute.type === PropertyType.List || selectedAttribute.type === PropertyType.Color) {
                this.listAttributeValueCtrl.setValidators(Validators.required);
            } else {
                this.inputCtrl.setValidators(Validators.required);
            }
        }

        // Le champ format doit être activé si l'attribut selectionné est de type Date ou Date & heure.
        if (selectedAttribute && (selectedAttribute.type === PropertyType.Date || selectedAttribute.type === PropertyType.DateAndTime)) {
            this.formatDisabled = false;
        } else {
            this.formatDisabled = true;

            // Ajout d'un validateur pour les attributs ayant un format de saisie ('Email', 'N° de téléphone', etc)
            if (selectedAttribute.format) {
                this.inputCtrl.addValidators(Validators.pattern(selectedAttribute.format));
            }
        }

        this.listAttributeValueCtrl.markAllAsTouched();
        this.listAttributeValueCtrl.updateValueAndValidity();
        this.inputCtrl.markAllAsTouched();
        this.inputCtrl.updateValueAndValidity();
    }

    private selectAttribute(attributeId: number) {
        this.selectedAttribute = this.data.attributes.find((x) => x.id == attributeId);

        if (this.selectedAttribute.type === PropertyType.List || this.selectedAttribute.type === PropertyType.ListCustomers) {
            if (this.selectedAttribute.name === 'FolderStateId') {
                this.listValues = this.data.folderStates.map((x) => {
                    let item = new ValueText();
                    item.value = x.id.toString();
                    item.text = x.name;
                    return item;
                });
            } else {
                this.listValues = this.selectedAttribute.attributeValueDefinitions.map((x) => {
                    let item = new ValueText();
                    item.value = x.id.toString();
                    item.text = x.name;
                    return item;
                });
            }
        }
    }

    /**
     * Initialise l'autocompletion pour un attribut.
     * @param attribute : Instance de l'attribut.
     */
    private initAutoCompletion(attribute: Attribute) {
        this.restApiConfigurationsService.get(attribute.configurationApiId).subscribe((x) => {
            this.restApiConfiguration = x;
        });

        this.autocompletionValues = this.listAttributeValueCtrl.valueChanges.pipe(
            tap((value) => console.log('Autocomplete : ', value)),
            switchMap((val) => {
                return this.getAutocompletionList(val || '');
            })
        );
    }

    /**
     * Retourne la liste des données correspondant à la saisie de l'utilisateur.
     * @param userInput : saisie de l'utilisateur
     * @returns : Une liste de données.
     */
    private getAutocompletionList(userInput: string): Observable<string[]> {
        return this.autocompletionService.getDatas(this.restApiConfiguration, userInput.toLowerCase());
    }
}
